import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { ChevronDown, ChevronUp, Edit2 } from "@piiqtechnologies/svg-icons";
import { PeekButton, PeekDivider, PeekIconButton, PeekStack, PeekTextField, PeekTooltip, PeekTypography, } from "@piiqtechnologies/ui-components";
import { useAnalytics } from "@piiqtechnologies/ui-lib";
import parentMessages from "../../messages.i18n";
import messages from "../messages.i18n";
const STEP = 100;
const MIN_VALUE = 0;
const MAX_VALUE = 99999;
const formatPrice = (value) => {
    return `$${Intl.NumberFormat().format(value)}`;
};
const parsePrice = (value) => {
    const digits = value.replace(/[^0-9]/g, "");
    return digits ? Number(digits) : 0;
};
export const PriceLayoutRow = ({ subtopics, defaultCount, onEdit, editingNewIndex, onEditingNewHandled, saveTrigger, category, onEditingCountChange, readOnly, hasBeenRun, }) => {
    const intl = useIntl();
    const analytics = useAnalytics();
    const [editingRows, setEditingRows] = useState({});
    useEffect(() => {
        onEditingCountChange === null || onEditingCountChange === void 0 ? void 0 : onEditingCountChange(Object.keys(editingRows).length);
    }, [editingRows, onEditingCountChange]);
    useEffect(() => {
        if (editingNewIndex !== null && editingNewIndex !== undefined) {
            setEditingRows((prev) => (Object.assign(Object.assign({}, prev), { [editingNewIndex]: { value: 0, isNew: true } })));
            onEditingNewHandled === null || onEditingNewHandled === void 0 ? void 0 : onEditingNewHandled();
        }
    }, [editingNewIndex, onEditingNewHandled]);
    const prevSaveTriggerRef = useRef(saveTrigger);
    useEffect(() => {
        if (saveTrigger === prevSaveTriggerRef.current)
            return;
        prevSaveTriggerRef.current = saveTrigger;
        if (!saveTrigger)
            return;
        Object.entries(editingRows).forEach(([key, row]) => {
            const index = Number(key);
            const isDefault = index < defaultCount;
            if (!row.value && isDefault)
                return;
            onEdit === null || onEdit === void 0 ? void 0 : onEdit(index, row.value ? formatPrice(row.value) : "");
        });
        setEditingRows({});
    }, [saveTrigger, editingRows, defaultCount, onEdit]);
    const handleStartEdit = (index) => {
        analytics.track("Custom Subtopics | Edit Clicked", { category, index });
        setEditingRows((prev) => (Object.assign(Object.assign({}, prev), { [index]: { value: parsePrice(subtopics[index]), isNew: false } })));
    };
    const handleCancelEdit = (index) => {
        const row = editingRows[index];
        analytics.track("Custom Subtopics | Edit Cancelled", { category, isNewRow: row === null || row === void 0 ? void 0 : row.isNew });
        if (row === null || row === void 0 ? void 0 : row.isNew) {
            onEdit === null || onEdit === void 0 ? void 0 : onEdit(index, "");
        }
        setEditingRows((prev) => {
            const next = Object.assign({}, prev);
            delete next[index];
            return next;
        });
    };
    const handleChange = (index, rawValue) => {
        const numValue = parsePrice(rawValue);
        if (numValue <= MAX_VALUE) {
            setEditingRows((prev) => (Object.assign(Object.assign({}, prev), { [index]: Object.assign(Object.assign({}, prev[index]), { value: numValue }) })));
        }
    };
    const handleIncrement = (index) => {
        setEditingRows((prev) => {
            var _a, _b;
            const current = (_b = (_a = prev[index]) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : 0;
            const newValue = Math.min(current + STEP, MAX_VALUE);
            return Object.assign(Object.assign({}, prev), { [index]: Object.assign(Object.assign({}, prev[index]), { value: newValue }) });
        });
    };
    const handleDecrement = (index) => {
        setEditingRows((prev) => {
            var _a, _b;
            const current = (_b = (_a = prev[index]) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : 0;
            const newValue = Math.max(current - STEP, MIN_VALUE);
            return Object.assign(Object.assign({}, prev), { [index]: Object.assign(Object.assign({}, prev[index]), { value: newValue }) });
        });
    };
    return (React.createElement(React.Fragment, null, subtopics.map((subtopic, index) => {
        const editing = editingRows[index];
        const displayValue = formatPrice(parsePrice(subtopic));
        return (React.createElement(React.Fragment, { key: `${index}-${subtopic}` },
            React.createElement(PeekStack, { direction: "row", justifyContent: "space-between", alignItems: "center", width: "100%" },
                React.createElement(PeekStack, { direction: "row", gap: 1, alignItems: "center", flex: 1 },
                    React.createElement(PeekTypography, { variant: "body1" },
                        index + 1,
                        "."),
                    editing ? (React.createElement(PeekStack, { direction: "row", gap: 0.5, alignItems: "center" },
                        React.createElement(PeekTextField, { autoFocus: true, size: "small", value: Intl.NumberFormat().format(editing.value), onChange: (e) => handleChange(index, e.target.value), sx: { width: 120 } }),
                        React.createElement(PeekStack, { direction: "column", gap: 0 },
                            React.createElement(PeekIconButton, { onClick: () => handleIncrement(index), bordered: false, sx: { p: 0, lineHeight: 0 } },
                                React.createElement(ChevronUp, { fontSize: 14 })),
                            React.createElement(PeekIconButton, { onClick: () => handleDecrement(index), bordered: false, sx: { p: 0, lineHeight: 0 } },
                                React.createElement(ChevronDown, { fontSize: 14 }))))) : (React.createElement(PeekTypography, { variant: "body1" }, displayValue))),
                editing ? (React.createElement(PeekButton, { size: "small", variant: "text", color: "error", onMouseDown: (e) => e.preventDefault(), onClick: () => handleCancelEdit(index) }, intl.formatMessage(messages.cancelButton))) : (!readOnly && (React.createElement(PeekTooltip, { title: hasBeenRun ? (React.createElement(PeekTypography, { variant: "body1" }, intl.formatMessage(parentMessages.hasBeenRunTooltip, {
                        bold: (chunks) => React.createElement("span", { style: { fontWeight: 500 } }, chunks),
                        br: () => React.createElement("br", null),
                    }))) : (React.createElement(PeekTypography, { variant: "body1" }, intl.formatMessage(messages.editTooltip))) },
                    React.createElement("span", null,
                        React.createElement(PeekIconButton, { onClick: () => handleStartEdit(index), bordered: false, sx: { p: 0.5 }, disabled: hasBeenRun },
                            React.createElement(Edit2, { fontSize: 14 }))))))),
            React.createElement(PeekDivider, { light: true })));
    })));
};
